import { axiosInstance } from "@/shared/lib/axiosInstance";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { SortDirection } from "./dataTableUtils";

interface UseDataTableOptions {
  rows: any[];
  url?: string;
  listReducer?: any;
  isPagination?: boolean;
  defaultSelectedRows?: any[];
  params?: Record<string, any>;
  sortable?: boolean;
  pageSize?: number;
  onSelectionChange?: (selectedRows: any[]) => void;
  refreshKey?: number;
  controlledFilters?: Record<string, string | number | null | undefined>;
  onFilterChange?: (name: string, value: string | number) => void;
  shouldFetch?: (requestBody: any) => boolean;
  /** Keep page/search/sort out of the query string (modal tables). */
  syncUrl?: boolean;
}

export function useDataTable(options: UseDataTableOptions) {
  const {
    rows,
    url,
    listReducer,
    isPagination = true,
    defaultSelectedRows,
    params = {},
    sortable = false,
    pageSize = 10,
    onSelectionChange,
    controlledFilters,
    onFilterChange,
    shouldFetch,
    syncUrl = true,
  } = options;

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /* =====================================================
      ✅ INITIAL STATE FROM URL
     ===================================================== */

  const initialPage = syncUrl ? Number(searchParams.get("page")) || 1 : 1;
  const initialSearch = syncUrl ? searchParams.get("search") || "" : "";
  const initialOrdering = syncUrl ? searchParams.get("ordering") : null;

  const [requestBody, setRequestBody] = useState<any>({
    page: initialPage,
    page_size: pageSize,
    search: initialSearch,
    ordering: initialOrdering,
    filters: {},
    ...params,
  });

  useEffect(() => {
    if (!controlledFilters) return;

    setRequestBody((prev: any) => {
      const filters = { ...(prev.filters || {}) };

      Object.entries(controlledFilters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          delete filters[key];
        } else {
          filters[key] = value;
        }
      });

      return {
        ...prev,
        filters,
        page: 1,
      };
    });
  }, [controlledFilters]);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(url));
  const requestSequenceRef = useRef(0);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null as boolean | null,
    previous: null as boolean | null,
  });

  /* =====================================================
      🔹 SYNC WHEN USER USES BACK/FORWARD BUTTON
     ===================================================== */

  useEffect(() => {
    if (!syncUrl) return;

    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("search") || "";
    const ordering = searchParams.get("ordering");

    setRequestBody((prev: any) => ({
      ...prev,
      page,
      search,
      ordering,
    }));
  }, [searchParams, syncUrl]);

  /* =====================================================
      🔹 UPDATE HELPERS
     ===================================================== */

  const updateUrlParams = (updates: Record<string, any>) => {
    if (!syncUrl) return;

    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === "" || value === undefined) updated.delete(key);
        else updated.set(key, String(value));
      });

      return updated;
    });
  };

  const handleSetPage = useCallback(
    (newPage: number) => {
      setRequestBody((prev: any) => ({
        ...prev,
        page: newPage,
      }));

      const currentOrdering = searchParams.get("ordering");
      updateUrlParams({ page: newPage, ordering: currentOrdering });
    },
    [searchParams]
  );

  const handleSearchChange = useCallback((q: string) => {
    setRequestBody((prev: any) => ({
      ...prev,
      search: q,
      page: 1,
    }));

    updateUrlParams({ search: q, page: 1 });
  }, []);

  const handleFilterChange = useCallback(
    (name: string, value: string | number) => {
      setRequestBody((prev: any) => {
        const filters = { ...(prev.filters || {}) };

        if (value === "" || value === null || value === undefined) {
          delete filters[name];
        } else {
          filters[name] = value;
        }

        return {
          ...prev,
          filters,
          page: 1,
        };
      });

      updateUrlParams({ page: 1 });
      onFilterChange?.(name, value);
    },
    [onFilterChange]
  );

  const handleSort = useCallback(
    (key: string) => {
      if (!sortable) return;

      setRequestBody((prev: any) => {
        let direction: SortDirection = "asc";

        if (prev.ordering === key) direction = "desc";
        else if (prev.ordering === `-${key}`) direction = null;

        const orderingValue = direction ? (direction === "desc" ? `-${key}` : key) : null;

        updateUrlParams({ ordering: orderingValue, page: 1 });

        return {
          ...prev,
          ordering: orderingValue,
          page: 1,
        };
      });
    },
    [sortable]
  );
  const getList = useCallback(async () => {
    if (!url) return;

    if (shouldFetch && !shouldFetch(requestBody)) {
      setIsLoading(false);
      return;
    }

    const currentRequest = ++requestSequenceRef.current;
    setIsLoading(true);

    try {
      const payload = {
        ...requestBody,
        page_size: pageSize,
      };

      const res = await axiosInstance.post(url, payload);
      const response = res.data;

      if (listReducer) {
        dispatch(listReducer(response));
      }

      if (response?.total !== undefined) {
        const totalPages = Math.ceil(response.total / response.page_size);

        setPagination({
          count: response.total,
          next: response.page < totalPages ? true : null,
          previous: response.page > 1 ? true : null,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      // Only resolve loading for the latest in-flight request.
      if (currentRequest === requestSequenceRef.current) {
        setIsLoading(false);
      }
    }
  }, [url, requestBody, pageSize, dispatch, listReducer, shouldFetch]);

  useEffect(() => {
    if (url) getList();
  }, [requestBody, url]);

  useEffect(() => {
    setRequestBody((prev: any) => {
      if (prev.page_size === pageSize && prev.page === 1) return prev;
      return {
        ...prev,
        page: 1,
        page_size: pageSize,
      };
    });

    updateUrlParams({ page: 1 });
  }, [pageSize]);

  /* =====================================================
      🔹 REFRESH DATA (when refreshKey changes)
     ===================================================== */

  const { refreshKey } = options;

  useEffect(() => {
    if (refreshKey && refreshKey > 0 && url) {
      getList();
    }
  }, [refreshKey]);

  const clientFilteredRows = useMemo(() => {
    // When a url is present the server handles search/filter.
    if (url) return rows;

    let filtered = rows;

    // ── Text search across all string fields ──────────────────
    const query = (requestBody.search || "").trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((row) =>
        Object.values(row).some((val) => {
          if (Array.isArray(val)) {
            return val.some((v) => String(v).toLowerCase().includes(query));
          }
          return String(val ?? "")
            .toLowerCase()
            .includes(query);
        })
      );
    }

    const activeFilters = requestBody.filters as Record<string, string>;
    Object.entries(activeFilters).forEach(([field, value]) => {
      if (!value) return;

      filtered = filtered.filter((row) => {
        const cellValue = row[field];
        if (Array.isArray(cellValue)) {
          return cellValue.some((v) => String(v).toLowerCase() === String(value).toLowerCase());
        }
        return String(cellValue ?? "").toLowerCase() === String(value).toLowerCase();
      });
    });

    return filtered;
  }, [rows, url, requestBody.search, requestBody.filters]);

  // const sortedRows = useMemo(() => {
  //   const source = url ? rows : clientFilteredRows;

  //   if (!sortable || !requestBody.ordering) return source;

  //   const isDesc = requestBody.ordering.startsWith("-");
  //   const key = isDesc ? requestBody.ordering.substring(1) : requestBody.ordering;
  //   console.log("rows", rows);
  //   console.log("clientFilteredRows", clientFilteredRows);
  //   console.log("source", source);

  //   return [...source]?.sort((a, b) => {
  //     const aValue = a[key];
  //     const bValue = b[key];

  //     if (aValue === bValue) return 0;

  //     const comparison =
  //       typeof aValue === "number" ? aValue - bValue : String(aValue).localeCompare(String(bValue));

  //     return isDesc ? -comparison : comparison;
  //   });
  // }, [rows, clientFilteredRows, url, requestBody.ordering, sortable]);
  const sortedRows = useMemo(() => {
    const source = Array.isArray(url ? rows : clientFilteredRows)
      ? url
        ? rows
        : clientFilteredRows
      : [];

    if (!sortable || !requestBody.ordering) return source;

    const isDesc = requestBody.ordering.startsWith("-");
    const key = isDesc ? requestBody.ordering.substring(1) : requestBody.ordering;

    return [...source].sort((a, b) => {
      const aValue = a?.[key];
      const bValue = b?.[key];

      if (aValue === bValue) return 0;

      const comparison =
        typeof aValue === "number"
          ? aValue - bValue
          : String(aValue ?? "").localeCompare(String(bValue ?? ""));

      return isDesc ? -comparison : comparison;
    });
  }, [rows, clientFilteredRows, url, requestBody.ordering, sortable]);
  const paginatedRows = useMemo(() => {
    if (url || !isPagination) return sortedRows;

    const start = (requestBody.page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, requestBody.page, url, isPagination, pageSize]);

  useEffect(() => {
    if (!url && isPagination) {
      const total = sortedRows.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      setPagination({
        count: total,
        next: requestBody.page < totalPages ? true : null,
        previous: requestBody.page > 1 ? true : null,
      });
    }
  }, [sortedRows, url, requestBody.page, isPagination, pageSize]);

  /* =====================================================
      🔹 ROW SELECTION
     ===================================================== */

  useEffect(() => {
    if (defaultSelectedRows?.length) {
      setSelectedRows(defaultSelectedRows);
    }
  }, [defaultSelectedRows]);

  const handleSelectRow = useCallback(
    (rowId: string | number) => {
      const stringRowId = String(rowId);
      const updated = selectedRows.includes(stringRowId)
        ? selectedRows.filter((id) => String(id) !== stringRowId)
        : [...selectedRows, stringRowId];

      setSelectedRows(updated);

      const selectedData = rows.filter((row) => updated.includes(String(row.id)));

      onSelectionChange?.(selectedData);
    },
    [rows, selectedRows, onSelectionChange]
  );

  const displayedRows = isPagination && url ? rows : paginatedRows;
  const isAllSelected =
    displayedRows?.length > 0 &&
    displayedRows.every((row) => selectedRows.includes(String(row.id)));

  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < displayedRows.length;

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedRows([]);
      onSelectionChange?.([]);
    } else {
      const allRowIds = displayedRows.map((row) => String(row.id));
      setSelectedRows(allRowIds);
      onSelectionChange?.(displayedRows);
    }
  }, [displayedRows, isAllSelected, onSelectionChange]);

  /* =====================================================
      🔹 RETURN
     ===================================================== */

  return {
    isLoading,
    requestBody,
    page: requestBody.page,
    sortedRows,
    paginatedRows,
    selectedRows,
    isAllSelected,
    isIndeterminate,
    pagination,
    handleSetPage,
    handleSearchChange,
    handleFilterChange,
    handleSelectRow,
    handleSelectAll,
    handleSort,
  };
}
